import {
	REALTIME_SAMPLE_RATE,
	pcm16Base64ToFloat,
	computeRms,
	rmsToLevel,
} from "@llmgateway/shared/realtime-pcm";

export {
	REALTIME_SAMPLE_RATE,
	resampleLinear,
	bytesToBase64,
	base64ToBytes,
	floatToPcm16Base64,
	pcm16ChunksToWavBase64,
	pcm16Base64ToFloat,
	computeRms,
	rmsToLevel,
} from "@llmgateway/shared/realtime-pcm";

export interface FlushResult {
	itemId: string | null;
	contentIndex: number;
	/**
	 * Milliseconds of the current assistant item actually played (not merely
	 * queued) — the audio_end_ms for conversation.item.truncate.
	 */
	playedMs: number;
}

/**
 * Sample-accurate scheduler for assistant PCM16 output. Buffers are chained
 * onto a running clock; timing is tracked per assistant item so barge-in
 * truncation uses the duration the listener actually heard rather than the
 * duration received. Everything is routed through an analyser so the UI can
 * meter what the assistant is actually saying.
 */
export class RealtimePlayback {
	private readonly context: AudioContext;
	private readonly analyser: AnalyserNode;
	private readonly levelSamples: Float32Array<ArrayBuffer>;
	private sources: AudioBufferSourceNode[] = [];
	private nextStartTime = 0;
	private currentItemId: string | null = null;
	private currentContentIndex = 0;
	private itemStartTime = 0;
	private itemScheduledSeconds = 0;

	public constructor(context: AudioContext) {
		this.context = context;
		this.analyser = context.createAnalyser();
		this.analyser.fftSize = 1024;
		this.analyser.smoothingTimeConstant = 0.6;
		this.analyser.connect(context.destination);
		this.levelSamples = new Float32Array(this.analyser.fftSize);
	}

	/**
	 * Current output loudness on the same 0..1 scale as the microphone meter.
	 */
	public getLevel(): number {
		if (this.sources.length === 0) {
			return 0;
		}
		this.analyser.getFloatTimeDomainData(this.levelSamples);
		return rmsToLevel(computeRms(this.levelSamples));
	}

	public enqueue(
		itemId: string,
		contentIndex: number,
		base64Pcm: string,
	): void {
		const samples = pcm16Base64ToFloat(base64Pcm);
		if (samples.length === 0) {
			return;
		}
		if (itemId !== this.currentItemId) {
			this.currentItemId = itemId;
			this.currentContentIndex = contentIndex;
			this.itemStartTime = Math.max(
				this.nextStartTime,
				this.context.currentTime,
			);
			this.itemScheduledSeconds = 0;
		}
		const buffer = this.context.createBuffer(
			1,
			samples.length,
			REALTIME_SAMPLE_RATE,
		);
		buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);
		const source = this.context.createBufferSource();
		source.buffer = buffer;
		source.connect(this.analyser);
		const startAt = Math.max(this.nextStartTime, this.context.currentTime);
		source.start(startAt);
		this.nextStartTime = startAt + buffer.duration;
		this.itemScheduledSeconds += buffer.duration;
		this.sources.push(source);
		source.onended = () => {
			this.sources = this.sources.filter((s) => s !== source);
		};
	}

	/**
	 * Stop all live and queued playback immediately and reset scheduling
	 * state. Returns the current item and how much of it was actually heard.
	 */
	public flush(): FlushResult {
		const now = this.context.currentTime;
		const result: FlushResult = {
			itemId: this.currentItemId,
			contentIndex: this.currentContentIndex,
			playedMs:
				this.currentItemId === null
					? 0
					: Math.round(
							Math.min(
								Math.max(0, now - this.itemStartTime),
								this.itemScheduledSeconds,
							) * 1000,
						),
		};
		for (const source of this.sources) {
			try {
				source.onended = null;
				source.stop();
			} catch {
				// Already ended.
			}
		}
		this.sources = [];
		this.nextStartTime = 0;
		this.currentItemId = null;
		this.currentContentIndex = 0;
		this.itemStartTime = 0;
		this.itemScheduledSeconds = 0;
		return result;
	}

	public get isPlaying(): boolean {
		return this.sources.length > 0;
	}
}

export interface MicrophoneCaptureHandles {
	stop: () => void;
}

/**
 * Served from apps/playground/public. Because it is a .js file under apps/,
 * both .gitignore and .dockerignore blanket-exclude it and need an explicit
 * negation — without one the deployed image 404s here and the failure surfaces
 * as an opaque WebSocket close.
 */
export const PCM_RECORDER_WORKLET_URL = "/worklets/pcm-recorder.js";

/**
 * Wire a microphone stream through the pcm-recorder AudioWorklet. The
 * worklet output goes through a zero-gain node so microphone audio is
 * processed without being played back locally. onChunk receives raw Float32
 * batches at the context's real sample rate.
 */
export async function startMicrophoneCapture(
	context: AudioContext,
	stream: MediaStream,
	onChunk: (samples: Float32Array) => void,
): Promise<MicrophoneCaptureHandles> {
	await context.audioWorklet.addModule(PCM_RECORDER_WORKLET_URL);
	const source = context.createMediaStreamSource(stream);
	const worklet = new AudioWorkletNode(context, "pcm-recorder", {
		numberOfInputs: 1,
		numberOfOutputs: 1,
		channelCount: 1,
	});
	const silent = context.createGain();
	silent.gain.value = 0;
	source.connect(worklet);
	worklet.connect(silent);
	silent.connect(context.destination);
	worklet.port.onmessage = (event: MessageEvent<Float32Array>) => {
		onChunk(event.data);
	};
	return {
		stop: () => {
			worklet.port.onmessage = null;
			try {
				source.disconnect();
			} catch {
				// Already disconnected.
			}
			try {
				worklet.disconnect();
			} catch {
				// Already disconnected.
			}
			try {
				silent.disconnect();
			} catch {
				// Already disconnected.
			}
		},
	};
}
